/**
 * AP-34 9. — **Abgleich der Eingangsrechnung gegen die Bestellung.** Reine Funktionen für Server
 * und Maske: der Vertrag „abrechenbare Position" (9.1 mit Nachtrag 1, 6.2), die Restmenge (9.2),
 * die Abweichungen je Zeile und der Abgleichstatus (9.3), die Zuordnung über Bezeichnungen (9.5).
 *
 * Die Quelle liefert Stammwerte, das Eingangsmodul rechnet die Restmenge: „bereits verrechnet" ist
 * eine Summe über Rechnungszeilen — eine Tatsache des Eingangsmoduls, nicht der Bestellung.
 */
import type { IncomingLineType, IncomingMatchStatus } from './incoming-invoices'

// ── Vertrag (9.1) ───────────────────────────────────────────────────────────

export type AbrechenbareQuelle = 'PURCHASE_ORDER_LINE' | 'GOODS_RECEIPT_LINE'

export interface AbrechenbarePosition {
  quelle: AbrechenbareQuelle
  /** `DocumentLine.id` bzw. künftige Wareneingangszeile */
  quelleId: string
  lieferantId: string
  bestellungId: string
  /** `documentNumber` oder `draftLabel` */
  bestellnummer: string
  /** ISO-Datum der Bestellung */
  bestelldatum: string | null
  /** `positionNo[.subPositionNo]` */
  bestellzeileNr: string
  lieferscheinNr: string | null
  lieferdatum: string | null
  articleId: string | null
  articleNumber: string | null
  supplierArticleNumber: string | null
  bezeichnung: string
  unitCode: string | null
  /** Dezimaltext */
  bestellteMenge: string
  /** `null` = die Quelle kennt keine Lieferung */
  gelieferteMenge: string | null
  einzelpreis: string
  preiseinheit: string
  rabattProzent: string
  steuersatz: string | null
  projectId: string | null
  projectPositionId: string | null
  /** `Document.clerkUserId ?? createdById` */
  bestellerUserId: string | null
  // ── Nachtrag 1, 6.2 — die bestätigte Auftragsbestätigung gilt als vereinbart ──
  /** in der Einheit der Bestellzeile */
  bestaetigterEinzelpreis: string | null
  bestaetigtePreiseinheit: string | null
  bestaetigterRabattProzent: string | null
  /** nur zur Anzeige */
  bestaetigteMenge: string | null
  bestaetigtAm: string | null
}

/** Was eine Quelle anbietet — das Belegwesen für Bestellzeilen, später das Warenmanagement. */
export interface AbrechenbarePositionenQuelle {
  readonly quelle: AbrechenbareQuelle
  offene(tenantId: string, lieferantId: string): Promise<AbrechenbarePosition[]>
  zeilen(tenantId: string, ids: string[]): Promise<AbrechenbarePosition[]>
}

/** 7.2 — Abweichungen an der Zeile. `SOURCE_EXHAUSTED` sperrt das Abschließen (10.1). */
export const INCOMING_LINE_DEVIATIONS = ['NO_SOURCE', 'QUANTITY_EXCEEDS_OPEN', 'PRICE_ABOVE_TOLERANCE', 'PRICE_BELOW_ORDER', 'UNIT_MISMATCH', 'SOURCE_EXHAUSTED', 'SOURCE_GONE'] as const
export type IncomingLineDeviation = (typeof INCOMING_LINE_DEVIATIONS)[number]

// ── Einheiten (9.2) ─────────────────────────────────────────────────────────

/**
 * Gleiche Einheit, andere Schreibweise ist keine Abweichung. Die Tabelle ist Code, nicht
 * Einstellung: Sie gilt für alle Mandanten gleich und wächst mit gemeldeten Fällen (27.2-30).
 * „lfm" ist bewusst **nicht** „m".
 */
export const EINHEIT_SYNONYME: readonly (readonly string[])[] = [
  ['stk', 'stück', 'stueck', 'st', 'pcs', 'pc', 'pce', 'ea', 'stck'],
  ['m2', 'm²', 'qm', 'quadratmeter'],
  ['m3', 'm³', 'cbm', 'kubikmeter'],
  ['lfm', 'lfdm', 'laufmeter', 'lm'],
  ['m', 'meter', 'mtr'],
  ['h', 'std', 'stunde', 'stunden', 'hr', 'hrs'],
  ['pausch', 'psch', 'pa', 'pauschale', 'pauschal', 'ps'],
  ['kg', 'kilo', 'kilogramm'],
  ['t', 'to', 'tonne', 'tonnen'],
  ['l', 'ltr', 'liter'],
  ['pkg', 'pack', 'packung', 'pkt', 'paket', 'vpe', 've'],
  ['pal', 'palette', 'paletten'],
  ['rol', 'rolle', 'rollen'],
  ['sack', 'sa', 'sck'],
  ['km', 'kilometer'],
  ['tag', 'tage', 'tg', 'd'],
]

const EINHEIT_INDEX = new Map<string, string>(EINHEIT_SYNONYME.flatMap((gruppe) => gruppe.map((wort) => [wort, gruppe[0]] as [string, string])))

/** Die Einheit in ihrer Vergleichsform; leer bleibt leer. */
export function einheitNormal(code: string | null | undefined): string {
  const roh = (code ?? '').trim().toLowerCase().replace(/\.+$/g, '').replace(/\.$/, '').replace(/\s+/g, '')
  const ohnePunkt = roh.replace(/\./g, '')
  return EINHEIT_INDEX.get(roh) ?? EINHEIT_INDEX.get(ohnePunkt) ?? ohnePunkt
}

/** Zwei leere Einheiten und eine leere gegen eine gefüllte gelten als gleich — ohne Angabe gibt es nichts zu beanstanden. */
export function einheitGleich(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = einheitNormal(a)
  const y = einheitNormal(b)
  return !x || !y || x === y
}

// ── Preis und Restmenge ─────────────────────────────────────────────────────

const zahl = (s: string | number | null | undefined): number => {
  const n = typeof s === 'number' ? s : Number(s ?? 0)
  return Number.isFinite(n) ? n : 0
}
const EPS = 0.00005

/** Nettoeinzelpreis je **eine** Einheit nach Rabatt. */
export function nettoJeEinheit(einzelpreis: number, preiseinheit: number, rabattProzent: number | null | undefined): number {
  const pe = preiseinheit > 0 ? preiseinheit : 1
  return (einzelpreis / pe) * (1 - (rabattProzent ?? 0) / 100)
}

/** Der Preis, gegen den verglichen wird: der bestätigte, wenn es einen gibt — sonst der bestellte (Nachtrag 1, 6.2). */
export function vergleichspreis(p: AbrechenbarePosition): { einzelpreis: number; preiseinheit: number; rabattProzent: number; bestaetigt: boolean } {
  if (p.bestaetigterEinzelpreis !== null) {
    return { einzelpreis: zahl(p.bestaetigterEinzelpreis), preiseinheit: zahl(p.bestaetigtePreiseinheit) || 1, rabattProzent: zahl(p.bestaetigterRabattProzent), bestaetigt: true }
  }
  return { einzelpreis: zahl(p.einzelpreis), preiseinheit: zahl(p.preiseinheit) || 1, rabattProzent: zahl(p.rabattProzent), bestaetigt: false }
}

/** 9.2 — `bezug(q) = gelieferteMenge ?? bestellteMenge`; `offen = bezug − verrechnet`. */
export function offeneMenge(p: Pick<AbrechenbarePosition, 'bestellteMenge' | 'gelieferteMenge'>, verrechnet: number): number {
  const bezug = p.gelieferteMenge !== null ? zahl(p.gelieferteMenge) : zahl(p.bestellteMenge)
  return Math.round((bezug - verrechnet) * 1000) / 1000
}

export interface AbgleichToleranz {
  /** Prozent nach oben */
  preisProzent: number
  /** Betrag je Zeile nach oben */
  preisBetrag: number
}

export interface AbgleichZeile {
  lineType: IncomingLineType
  quantity: number
  unitCode: string | null
  unitPrice: number
  priceUnit: number
  discountPercent: number | null
}

/**
 * 9.3 — die Abweichungen **einer** Zeile gegen ihren Bezug.
 *
 * @param offenOhneDieseRechnung offene Menge der Position, ohne die Zeilen dieser Rechnung
 * @param mengeDieserRechnung    Summe der Mengen aller Zeilen dieser Rechnung auf dieselbe Position
 */
export function zeilenAbweichungen(
  zeile: AbgleichZeile,
  bezug: AbrechenbarePosition | null,
  offenOhneDieseRechnung: number,
  mengeDieserRechnung: number,
  toleranz: AbgleichToleranz,
): IncomingLineDeviation[] {
  if (!bezug) return []
  const aus: IncomingLineDeviation[] = []
  if (!einheitGleich(zeile.unitCode, bezug.unitCode)) {
    // Unterschiedliche Einheiten: kein Umrechnen — Menge und Preis sind dann nicht vergleichbar.
    aus.push('UNIT_MISMATCH')
    return aus
  }
  // Eine Gutschrift (negative Menge) mindert das Verrechnete und kann nichts überschreiten.
  if (zeile.quantity > 0) {
    if (offenOhneDieseRechnung <= EPS) aus.push('SOURCE_EXHAUSTED')
    else if (mengeDieserRechnung - offenOhneDieseRechnung > EPS) aus.push('QUANTITY_EXCEEDS_OPEN')
  }
  const soll = vergleichspreis(bezug)
  const sollNetto = nettoJeEinheit(soll.einzelpreis, soll.preiseinheit, soll.rabattProzent)
  const istNetto = nettoJeEinheit(zeile.unitPrice, zeile.priceUnit, zeile.discountPercent)
  const differenz = istNetto - sollNetto
  if (differenz > EPS) {
    const prozent = sollNetto > 0 ? (differenz / sollNetto) * 100 : Infinity
    const betrag = Math.abs(differenz * zeile.quantity)
    // Innerhalb der Toleranz liegt, was die Prozentgrenze **oder** die Betragsgrenze einhält.
    if (prozent - toleranz.preisProzent > 1e-9 && betrag - toleranz.preisBetrag > 0.004) aus.push('PRICE_ABOVE_TOLERANCE')
  } else if (differenz < -EPS) {
    aus.push('PRICE_BELOW_ORDER')
  }
  return aus
}

const SPERRT_ABGLEICH: readonly string[] = ['NO_SOURCE', 'QUANTITY_EXCEEDS_OPEN', 'PRICE_ABOVE_TOLERANCE', 'UNIT_MISMATCH', 'SOURCE_EXHAUSTED', 'SOURCE_GONE']

/**
 * 9.3 — Abgleichstatus aus den Zeilen (Bedingungen 1–4). Die Bedingungen 5 und 6 — Summen und
 * andere Anlässe — prüft `pruefeEingangsrechnung`; „eindeutig abgeglichen" im Sinn des Abschließens
 * ist `MATCHED` **und** kein Freigabeanlass.
 */
export function abgleichStatus(
  zeilen: readonly { lineType: IncomingLineType; hatBezug: boolean; lineNet: number; deviations: readonly string[] }[],
  nebenkostenFreigrenze: number,
): { status: IncomingMatchStatus; nebenkostenUeberFreigrenze: boolean } {
  const wert = zeilen.filter((z) => z.lineType === 'ITEM' || z.lineType === 'CHARGE')
  if (!wert.some((z) => z.hatBezug)) return { status: 'NONE', nebenkostenUeberFreigrenze: false }
  const nebenkosten = wert.filter((z) => z.lineType === 'CHARGE' && !z.hatBezug).reduce((s, z) => s + z.lineNet, 0)
  const ueber = Math.round(nebenkosten * 100) > Math.round(nebenkostenFreigrenze * 100)
  const sauber = wert.every((z) => (z.lineType === 'CHARGE' || z.hatBezug) && !z.deviations.some((d) => SPERRT_ABGLEICH.includes(d)))
  return { status: sauber && !ueber ? 'MATCHED' : 'DEVIATION', nebenkostenUeberFreigrenze: ueber }
}

// ── Zuordnung über Bezeichnungen (9.5) ──────────────────────────────────────

/** Schwellen an **einer** Stelle (27.2-29): Die KI-Probe misst sie mit und darf sie ändern. */
export const ZUORDNUNG_SCHWELLE = 0.6
export const ZUORDNUNG_ABSTAND = 0.15
export const ZUORDNUNG_AUSWAHL = 3

const MASSEINHEIT = '(mm|cm|dm|m2|m3|m|kg|g|l|ml|kw|w|v|a|bar|zoll|")'

/**
 * Bezeichnung in Wörter: klein, Umlaute ausgeschrieben, Satzzeichen weg, „12,5" = „12.5",
 * Maßeinheiten an die Zahl gebunden („12,5 mm" → `12.5mm`).
 *
 * Maße sind einzelne Zahlen, gleich wie sie geschrieben sind: „40x5", „40 x 5" und „40×5" ergeben `40` und `5`
 * (Nachtrag 3, Praxistest). Vorher blieb „40x5" ein Wort und „40×5" zerfiel — der Flachstahl der Rechnung fand
 * den der Bestellung nicht, weil der Lieferant das Malzeichen setzt und der Besteller ein x tippt.
 */
export function bezeichnungWoerter(text: string | null | undefined): string[] {
  let t = (text ?? '').toLowerCase()
  t = t.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/²/g, '2').replace(/³/g, '3')
  t = t.replace(/(\d),(\d)/g, '$1.$2')
  t = t.replace(/(\d)\s*[x×]\s*(?=\d)/g, '$1 ')
  t = t.replace(new RegExp(`(\\d+(?:\\.\\d+)?)\\s*${MASSEINHEIT}(?![a-z0-9])`, 'g'), '$1$2')
  // Maße „100x50", „100 x 50" bleiben als Zahlen stehen; alles andere außer Buchstaben, Ziffern und Punkt in Zahlen wird Trenner
  t = t.replace(/[^a-z0-9.]+/g, ' ').replace(/(^|\s)\.+|\.+(\s|$)/g, ' ')
  return t.split(/\s+/).filter((w) => w.length > 1 || /\d/.test(w))
}

const istMass = (w: string) => new RegExp(`^\\d+(?:\\.\\d+)?${MASSEINHEIT}$`).test(w)
const massEinheit = (w: string) => w.replace(/^[\d.]+/, '')

/** Anteil gemeinsamer Wörter (Jaccard). Zahlen mit Einheit müssen übereinstimmen — sonst 0. */
export function textAehnlichkeit(a: string | null | undefined, b: string | null | undefined): number {
  const x = new Set(bezeichnungWoerter(a))
  const y = new Set(bezeichnungWoerter(b))
  if (!x.size || !y.size) return 0
  // „Gipskartonplatte 12,5 mm" darf nicht der 15-mm-Platte zugeordnet werden
  for (const w of x) {
    if (!istMass(w)) continue
    const gleicheEinheit = [...y].filter((v) => istMass(v) && massEinheit(v) === massEinheit(w))
    if (gleicheEinheit.length && !gleicheEinheit.includes(w)) return 0
  }
  let gemeinsam = 0
  for (const w of x) if (y.has(w)) gemeinsam++
  return gemeinsam / (x.size + y.size - gemeinsam)
}

export interface ZuordnungsZeile extends AbgleichZeile {
  id: string
  name: string
  articleId: string | null
  articleNumber: string | null
  supplierArticleNumber: string | null
}

export interface OffenePosition extends AbrechenbarePosition {
  /** offene Menge (9.2) */
  offen: number
}

/** 9.5 Stufe 3 — Punktwert einer Paarung: Text 0–0,6 · Einheit 0,1 · Menge 0,15 · Preis 0,15. */
export function zuordnungsWert(zeile: ZuordnungsZeile, position: OffenePosition, toleranz: AbgleichToleranz): number {
  const text = textAehnlichkeit(zeile.name, position.bezeichnung) * 0.6
  const einheit = einheitNormal(zeile.unitCode) && einheitNormal(zeile.unitCode) === einheitNormal(position.unitCode) ? 0.1 : 0
  const menge = zeile.quantity > 0 && zeile.quantity - position.offen <= EPS ? 0.15 : 0
  const soll = vergleichspreis(position)
  const sollNetto = nettoJeEinheit(soll.einzelpreis, soll.preiseinheit, soll.rabattProzent)
  const istNetto = nettoJeEinheit(zeile.unitPrice, zeile.priceUnit, zeile.discountPercent)
  // Hier geht es ums Erkennen, nicht ums Prüfen: mindestens ± 5 %
  const grenze = Math.max(toleranz.preisProzent, 5) / 100
  const preis = sollNetto > 0 && Math.abs(istNetto - sollNetto) / sollNetto <= grenze + 1e-9 ? 0.15 : 0
  return Math.round((text + einheit + menge + preis) * 1000) / 1000
}

export interface ZuordnungsVorschlag {
  lineId: string
  /** vorgeschlagene Position — `null`, wenn keine eindeutig ist */
  quelleId: string | null
  /** `ARTICLE` (Stufe 2, gilt als bestätigt) · `SCORE` (Stufe 3, zu bestätigen) */
  grund: 'ARTICLE' | 'SCORE' | null
  /** die besten Paarungen, absteigend — für die Auswahl von Hand */
  auswahl: { quelleId: string; wert: number }[]
}

/**
 * 9.5 Stufen 2 und 3 für Zeilen ohne Bezug. Vergeben wird von der besten Paarung abwärts; jede
 * offene Position höchstens so oft, wie ihre offene Menge reicht. **Im Zweifel keine Paarung.**
 */
export function schlageZuordnungenVor(zeilen: readonly ZuordnungsZeile[], positionen: readonly OffenePosition[], toleranz: AbgleichToleranz): ZuordnungsVorschlag[] {
  const rest = new Map(positionen.map((p) => [p.quelleId, p.offen]))
  const aus = new Map<string, ZuordnungsVorschlag>(zeilen.map((z) => [z.id, { lineId: z.id, quelleId: null, grund: null, auswahl: [] }]))
  const nimm = (z: ZuordnungsZeile, p: OffenePosition, grund: 'ARTICLE' | 'SCORE') => {
    const v = aus.get(z.id)!
    v.quelleId = p.quelleId
    v.grund = grund
    rest.set(p.quelleId, (rest.get(p.quelleId) ?? 0) - Math.max(z.quantity, 0))
  }
  const reicht = (z: ZuordnungsZeile, p: OffenePosition) => (rest.get(p.quelleId) ?? 0) - Math.max(z.quantity, 0) >= -EPS

  // Stufe 2 — Artikelnummer: eindeutig, wenn genau eine offene Position diesen Artikel trägt
  const norm = (s: string | null) => (s ?? '').trim().toUpperCase()
  for (const z of zeilen) {
    const treffer = positionen.filter(
      (p) =>
        p.offen > EPS &&
        ((z.articleId && p.articleId === z.articleId) ||
          (norm(z.articleNumber) && norm(p.articleNumber) === norm(z.articleNumber)) ||
          (norm(z.supplierArticleNumber) && norm(p.supplierArticleNumber) === norm(z.supplierArticleNumber))),
    )
    if (treffer.length === 1 && reicht(z, treffer[0])) nimm(z, treffer[0], 'ARTICLE')
  }

  // Stufe 3 — Bezeichnung, Menge, Einheit, Preis
  const paarungen: { z: ZuordnungsZeile; p: OffenePosition; wert: number }[] = []
  for (const z of zeilen) {
    const v = aus.get(z.id)!
    const werte = positionen
      .filter((p) => p.offen > EPS)
      .map((p) => ({ p, wert: zuordnungsWert(z, p, toleranz) }))
      .filter((w) => w.wert > 0)
      .sort((a, b) => b.wert - a.wert)
    v.auswahl = werte.slice(0, ZUORDNUNG_AUSWAHL).map((w) => ({ quelleId: w.p.quelleId, wert: w.wert }))
    if (v.quelleId || !werte.length) continue
    const [beste, zweite] = werte
    if (beste.wert >= ZUORDNUNG_SCHWELLE - 1e-9 && beste.wert - (zweite?.wert ?? 0) >= ZUORDNUNG_ABSTAND - 1e-9) paarungen.push({ z, p: beste.p, wert: beste.wert })
  }
  paarungen.sort((a, b) => b.wert - a.wert)
  for (const { z, p } of paarungen) if (reicht(z, p)) nimm(z, p, 'SCORE')
  return [...aus.values()]
}
